import React, { lazy, Suspense, useEffect } from "react";
import { useParams, Navigate } from "react-router-dom";
import { SEOHead } from "@/components/SEOHead";
import Navbar from "@/components/Navbar";
import Hero from "@/components/Hero";
import LogoMarquee from "@/components/LogoMarquee";
import { Button } from "@/components/ui/button";
import { LazySection } from "@/components/LazySection";

const Testimonials = lazy(() => import("@/components/Testimonials"));
const CTASection = lazy(() => import("@/components/CTASection"));
const HowItWorks = lazy(() => import("@/components/HowItWorks"));
const Comparison = lazy(() => import("@/components/Comparison"));
const PricingEducation = lazy(() => import("@/components/PricingEducation"));
const Pricing = lazy(() => import("@/components/Pricing"));
const CoHostComparison = lazy(() => import("@/components/CoHostComparison"));
const WinterOccupancy = lazy(() => import("@/components/WinterOccupancy"));
const FounderBio = lazy(() => import("@/components/FounderBio"));
const FAQ = lazy(() => import("@/components/FAQ"));
const Footer = lazy(() => import("@/components/Footer"));
const CountyCompliance = lazy(() => import("@/components/CountyCompliance"));
const FullServiceManagement = lazy(
  () => import("@/components/FullServiceManagement"),
);
const DCNeighborhoods = lazy(() => import("@/components/DCNeighborhoods"));

const locationData = {
  alexandria: {
    name: "Alexandria",
    title: "Alexandria Airbnb Management | JNN Homes",
    desc: "Expert Airbnb management in Alexandria, VA. Maximize your short-term rental revenue with our full-service local team.",
    heroTitle: (
      <>
        Expert Airbnb Management in{" "}
        <span className="text-primary italic">Alexandria</span>
      </>
    ),
    heroSubtitle:
      "Turn your Alexandria property into a high-earning asset. We handle permits, pricing, cleaning, and 24/7 guest support.",
  },
  arlington: {
    name: "Arlington",
    title: "Arlington Airbnb Management | JNN Homes",
    desc: "Expert Airbnb management in Arlington, VA. Maximize your short-term rental revenue with our full-service local team.",
    heroTitle: (
      <>
        Expert Airbnb Management in{" "}
        <span className="text-primary italic">Arlington</span>
      </>
    ),
    heroSubtitle:
      "Turn your Arlington property into a high-earning asset. We handle pricing, cleaning, compliance, and 24/7 guest support.",
  },
  fairfax: {
    name: "Fairfax",
    title: "Fairfax Airbnb Management | JNN Homes",
    desc: "Expert Airbnb management in Fairfax, VA. Maximize your short-term rental revenue with our full-service local team.",
    heroTitle: (
      <>
        Expert Airbnb Management in{" "}
        <span className="text-primary italic">Fairfax</span>
      </>
    ),
    heroSubtitle:
      "Turn your Fairfax property into a high-earning asset. We navigate local compliance, pricing, cleaning, and 24/7 guest support.",
  },
  loudoun: {
    name: "Loudoun County",
    title: "Loudoun County Airbnb Management | JNN Homes",
    desc: "Expert Airbnb management in Loudoun County, VA. Maximize your short-term rental revenue with our full-service local team.",
    heroTitle: (
      <>
        Expert Airbnb Management in{" "}
        <span className="text-primary italic">Loudoun County</span>
      </>
    ),
    heroSubtitle:
      "Turn your Loudoun County property into a high-earning asset. We handle pricing, cleaning, and 24/7 guest support.",
  },
  "prince-william": {
    name: "Prince William County",
    title: "Prince William County Airbnb Management | JNN Homes",
    desc: "Expert Airbnb management in Prince William County, VA. Maximize your short-term rental revenue with our full-service local team.",
    heroTitle: (
      <>
        Expert Airbnb Management in{" "}
        <span className="text-primary italic">Prince William County</span>
      </>
    ),
    heroSubtitle:
      "Turn your Prince William County property into a high-earning asset. We handle pricing, cleaning, and 24/7 guest support.",
  },
  "washington-dc": {
    name: "Washington, DC",
    title: "Airbnb Management Washington, DC | Neighborhoods | JNN Homes",
    desc: "Airbnb management for DC homeowners in Capitol Hill, Navy Yard, Dupont Circle, Shaw, Georgetown and more. Licensing guidance, pricing, cleaning and guest care.",
    heroTitle: (
      <>
        Expert Airbnb Management in{" "}
        <span className="text-primary italic">Washington, DC</span>
      </>
    ),
    heroSubtitle:
      "Hosting your own D.C. home on Airbnb, whether that's a room, an English basement, or the whole place while you travel? We handle the pricing, the cleanings and the guest messages, and we help you stay on the right side of DC's short-term rental rules.",
  },
};

const Location = ({ city }: { city?: string }) => {
  const { city: paramCity } = useParams();
  const currentCity = city || paramCity;
  const data = locationData[currentCity as keyof typeof locationData];

  if (!data) return <Navigate to="/404" />;

  const scrollToForm = () => {
    const formElement = document.getElementById("lead-form");
    if (formElement) {
      window.dispatchEvent(new Event("force-load-form"));
      formElement.scrollIntoView({ behavior: "smooth" });
    }
  };

  return (
    <div className="min-h-screen bg-background font-body selection:bg-primary/30 selection:text-primary">
      <SEOHead
        title={data.title}
        description={data.desc}
        canonical={`https://www.jnnhomes.com/airbnb-management-${currentCity}`}
        schemaJson={[
          {
            "@context": "https://schema.org",
            "@type": "LocalBusiness",
            name: `JNN Homes — Airbnb Management ${data.name}`,
            description: data.desc,
            url: `https://www.jnnhomes.com/airbnb-management-${currentCity}`,
            telephone: "+17038905425",
            areaServed: {
              "@type": "City",
              name:
                currentCity === "washington-dc"
                  ? "Washington, DC"
                  : `${data.name}, VA`,
            },
            parentOrganization: {
              "@type": "Organization",
              name: "JNN Homes",
              url: "https://www.jnnhomes.com",
            },
          },
          {
            "@context": "https://schema.org",
            "@type": "Service",
            serviceType: "Airbnb & Short-Term Rental Management",
            provider: {
              "@type": "LocalBusiness",
              name: "JNN Homes",
              url: "https://www.jnnhomes.com",
              telephone: "+17038905425",
            },
            areaServed: {
              "@type": "City",
              name:
                currentCity === "washington-dc"
                  ? "Washington, DC"
                  : `${data.name}, VA`,
            },
            url: `https://www.jnnhomes.com/airbnb-management-${currentCity}`,
            description: data.desc,
          },
          {
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              {
                "@type": "ListItem",
                position: 1,
                name: "Home",
                item: "https://www.jnnhomes.com",
              },
              {
                "@type": "ListItem",
                position: 2,
                name: `Airbnb Management ${data.name}`,
                item: `https://www.jnnhomes.com/airbnb-management-${currentCity}`,
              },
            ],
          },
        ]}
      />
      <Navbar />

      <main>
        <Hero title={data.heroTitle} subtitle={data.heroSubtitle} />

        <LogoMarquee />

        <LazySection
          minHeight="800px"
          fallback={
            <div className="h-[800px] hidden md:block bg-muted/5 animate-pulse rounded-xl mx-4 my-8" />
          }
        >
          <Suspense
            fallback={
              <div className="h-[800px] hidden md:block bg-muted/5 animate-pulse rounded-xl mx-4 my-8" />
            }
          >
            <FounderBio />
          </Suspense>
        </LazySection>

        <LazySection
          minHeight="600px"
          fallback={
            <div className="h-[600px] hidden md:block bg-muted/5 animate-pulse rounded-xl mx-4 my-8" />
          }
        >
          <Suspense
            fallback={
              <div className="h-[600px] hidden md:block bg-muted/5 animate-pulse rounded-xl mx-4 my-8" />
            }
          >
            <Testimonials variant={1} />
          </Suspense>
        </LazySection>

        <LazySection
          minHeight="600px"
          fallback={
            <div className="h-[600px] hidden md:block bg-muted/5 animate-pulse rounded-xl mx-4 my-8" />
          }
        >
          <Suspense
            fallback={
              <div className="h-[600px] hidden md:block bg-muted/5 animate-pulse rounded-xl mx-4 my-8" />
            }
          >
            <HowItWorks />
          </Suspense>
        </LazySection>

        <div className="py-12 bg-background flex justify-center px-4">
          <Button
            onClick={scrollToForm}
            className="bg-primary text-primary-foreground hover:bg-primary/90 text-base md:text-lg font-bold py-4 md:py-6 px-6 md:px-10 h-auto rounded-full w-full max-w-md md:w-auto whitespace-normal text-center"
          >
            👉 See My Airbnb Income — Takes 1 Minute
          </Button>
        </div>

        <LazySection
          minHeight="800px"
          fallback={
            <div className="h-[800px] hidden md:block bg-muted/5 animate-pulse rounded-xl mx-4 my-8" />
          }
        >
          <Suspense
            fallback={
              <div className="h-[800px] hidden md:block bg-muted/5 animate-pulse rounded-xl mx-4 my-8" />
            }
          >
            <Comparison />
          </Suspense>
        </LazySection>

        <LazySection
          minHeight="600px"
          fallback={
            <div className="h-[600px] hidden md:block bg-muted/5 animate-pulse rounded-xl mx-4 my-8" />
          }
        >
          <Suspense
            fallback={
              <div className="h-[600px] hidden md:block bg-muted/5 animate-pulse rounded-xl mx-4 my-8" />
            }
          >
            <PricingEducation />
          </Suspense>
        </LazySection>

        <LazySection
          minHeight="600px"
          rootMargin="400px"
          fallback={
            <div className="h-[600px] hidden md:block bg-muted/5 animate-pulse rounded-xl mx-4 my-8" />
          }
        >
          <Suspense
            fallback={
              <div className="h-[600px] hidden md:block bg-muted/5 animate-pulse rounded-xl mx-4 my-8" />
            }
          >
            <WinterOccupancy />
          </Suspense>
        </LazySection>

        <LazySection
          minHeight="800px"
          fallback={
            <div className="h-[800px] hidden md:block bg-muted/5 animate-pulse rounded-xl mx-4 my-8" />
          }
        >
          <Suspense
            fallback={
              <div className="h-[800px] hidden md:block bg-muted/5 animate-pulse rounded-xl mx-4 my-8" />
            }
          >
            <Pricing />
          </Suspense>
        </LazySection>

        <LazySection
          minHeight="600px"
          fallback={
            <div className="h-[600px] hidden md:block bg-muted/5 animate-pulse rounded-xl mx-4 my-8" />
          }
        >
          <Suspense
            fallback={
              <div className="h-[600px] hidden md:block bg-muted/5 animate-pulse rounded-xl mx-4 my-8" />
            }
          >
            <CoHostComparison />
          </Suspense>
        </LazySection>

        <LazySection
          minHeight="600px"
          fallback={
            <div className="h-[600px] hidden md:block bg-muted/5 animate-pulse rounded-xl mx-4 my-8" />
          }
        >
          <Suspense
            fallback={
              <div className="h-[600px] hidden md:block bg-muted/5 animate-pulse rounded-xl mx-4 my-8" />
            }
          >
            <CountyCompliance />
          </Suspense>
        </LazySection>

        <LazySection
          minHeight="800px"
          fallback={
            <div className="h-[800px] hidden md:block bg-muted/5 animate-pulse rounded-xl mx-4 my-8" />
          }
        >
          <Suspense
            fallback={
              <div className="h-[800px] hidden md:block bg-muted/5 animate-pulse rounded-xl mx-4 my-8" />
            }
          >
            <FullServiceManagement />
          </Suspense>
        </LazySection>

        <LazySection
          minHeight="600px"
          fallback={
            <div className="h-[600px] hidden md:block bg-muted/5 animate-pulse rounded-xl mx-4 my-8" />
          }
        >
          <Suspense
            fallback={
              <div className="h-[600px] hidden md:block bg-muted/5 animate-pulse rounded-xl mx-4 my-8" />
            }
          >
            <Testimonials variant={2} />
          </Suspense>
        </LazySection>

        {/* DC Neighborhoods — Washington, D.C. only */}
        {currentCity === "washington-dc" && (
          <LazySection
            minHeight="600px"
            fallback={
              <div className="h-[600px] hidden md:block bg-muted/5 animate-pulse rounded-xl mx-4 my-8" />
            }
          >
            <Suspense
              fallback={
                <div className="h-[600px] hidden md:block bg-muted/5 animate-pulse rounded-xl mx-4 my-8" />
              }
            >
              <DCNeighborhoods />
            </Suspense>
          </LazySection>
        )}

        <LazySection
          minHeight="800px"
          fallback={
            <div className="h-[800px] hidden md:block bg-muted/5 animate-pulse rounded-xl mx-4 my-8" />
          }
        >
          <Suspense
            fallback={
              <div className="h-[800px] hidden md:block bg-muted/5 animate-pulse rounded-xl mx-4 my-8" />
            }
          >
            <FAQ includeDCLicense={currentCity === "washington-dc"} />
          </Suspense>
        </LazySection>

        <LazySection
          minHeight="400px"
          fallback={
            <div className="h-[400px] hidden md:block bg-muted/5 animate-pulse rounded-xl mx-4 my-8" />
          }
        >
          <Suspense
            fallback={
              <div className="h-[400px] hidden md:block bg-muted/5 animate-pulse rounded-xl mx-4 my-8" />
            }
          >
            <CTASection />
          </Suspense>
        </LazySection>
      </main>

      <LazySection
        minHeight="160px"
        rootMargin="200px"
        fallback={
          <div className="h-40 hidden md:block bg-card/20 animate-pulse" />
        }
      >
        <Suspense
          fallback={
            <div className="h-40 hidden md:block bg-card/20 animate-pulse" />
          }
        >
          <Footer />
        </Suspense>
      </LazySection>
    </div>
  );
};

export default Location;
